import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Shield, Trash2, UserPlus, Loader2, DollarSign, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin — Admin Emails manager.
 * Lists all admin users; supports promoting new emails and demoting existing admins.
 * Wired to /api/admin/admins, /api/admin/users/promote, /api/admin/users/{id}/demote.
 */
export function AdminEmailsTab() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/admins`, { withCredentials: true });
      setAdmins(r.data?.admins || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load admins');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes('@')) { toast.error('Enter a valid email'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/users/promote`, { email: e }, { withCredentials: true });
      toast.success(r.data.created ? `Admin created (temp password: ${r.data.temp_password})` : 'Promoted to admin');
      setEmail('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add admin');
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Revoke admin from ${u.email}?`)) return;
    try {
      await axios.post(`${API}/admin/users/${u.user_id}/demote`, {}, { withCredentials: true });
      toast.success(`${u.email} demoted`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Demote failed');
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-emails-tab">
      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
        <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#06B6D4]" /> Admin Emails
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Grant admin access to any email. Existing users are instantly promoted; new emails get a stub account with a temporary password shown once.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !busy && add()}
            placeholder="new-admin@example.com"
            className="flex-1 bg-[#1E1E24] border-[#2E2E34] text-white h-9"
            data-testid="admin-add-email-input"
          />
          <Button onClick={add} disabled={busy || !email.trim()} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 h-9" data-testid="admin-add-email-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Add Admin</>}
          </Button>
        </div>
      </div>

      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#1E1E24] flex items-center justify-between">
          <h4 className="text-sm font-semibold text-white">Current Admins ({admins.length})</h4>
        </div>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#06B6D4]" /></div>
        ) : (
          <ul className="divide-y divide-[#1E1E24]">
            {admins.map(a => (
              <li key={a.user_id} className="px-5 py-3 flex items-center justify-between" data-testid={`admin-row-${a.user_id}`}>
                <div>
                  <p className="text-sm text-white">{a.email}</p>
                  <p className="text-[10px] font-mono text-zinc-500">{a.user_id}</p>
                </div>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => remove(a)}
                  className="text-red-400 hover:text-red-300 text-xs"
                  data-testid={`admin-demote-${a.user_id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
        <h4 className="text-sm font-semibold text-white mb-2">Email routing note</h4>
        <p className="text-xs text-zinc-400 leading-relaxed">
          Support tickets are stored in the database and mirrored to the fallback address
          <code className="mx-1 px-1 rounded bg-[#1E1E24] text-[#06B6D4]">bloomraynexus@gmail.com</code>.
          To have a custom domain like <code className="mx-1 px-1 rounded bg-[#1E1E24] text-[#06B6D4]">@playmold.com</code> forward to your admin
          inbox, configure a forwarding rule with your DNS/email provider (Google Workspace, Cloudflare Email Routing, etc.) — the app itself does not run an SMTP server.
        </p>
      </div>
    </div>
  );
}

/**
 * Admin — Marketplace pricing.
 * Lists every published marketplace asset and lets admin override its USD price.
 * Set to 0 (or blank) to make it free.
 */
export function AdminMarketplaceTab() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editingPrice, setEditingPrice] = useState({});
  // "All assets" browser — for publishing / removing any user's asset
  const [showBrowser, setShowBrowser] = useState(false);
  const [allAssets, setAllAssets] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [browserQuery, setBrowserQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/marketplace_assets`, { withCredentials: true });
      setAssets(r.data?.assets || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load marketplace');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadAllAssets = async (q = '') => {
    setAllLoading(true);
    try {
      const r = await axios.get(`${API}/admin/all_assets${q ? `?q=${encodeURIComponent(q)}` : ''}`, { withCredentials: true });
      setAllAssets(r.data?.assets || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load assets');
    } finally { setAllLoading(false); }
  };

  const publish = async (a) => {
    try {
      await axios.post(`${API}/assets/${a.asset_id}/publish`, {}, { withCredentials: true });
      toast.success(`Added "${a.name || a.prompt?.slice(0, 30) || 'asset'}" to Marketplace`);
      await load();
      await loadAllAssets(browserQuery);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    }
  };

  const unpublish = async (a) => {
    if (!window.confirm(`Remove "${a.name || a.prompt?.slice(0, 30) || 'asset'}" from Marketplace? (Owner still keeps it in their library.)`)) return;
    try {
      await axios.post(`${API}/assets/${a.asset_id}/unpublish`, {}, { withCredentials: true });
      toast.success('Removed from Marketplace');
      setAssets(prev => prev.filter(x => x.asset_id !== a.asset_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unpublish failed');
    }
  };

  const hardDelete = async (a) => {
    if (!window.confirm(`PERMANENTLY DELETE this asset for everyone (including its owner)?\n\n"${a.name || a.prompt?.slice(0, 60) || 'asset'}"\n\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/marketplace_assets/${a.asset_id}`, { withCredentials: true });
      toast.success('Asset permanently deleted');
      setAssets(prev => prev.filter(x => x.asset_id !== a.asset_id));
      setAllAssets(prev => prev.filter(x => x.asset_id !== a.asset_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const save = async (a) => {
    const raw = editingPrice[a.asset_id];
    const price = raw === '' || raw === undefined ? 0 : parseFloat(raw);
    if (isNaN(price) || price < 0 || price > 999) { toast.error('Price must be 0–999'); return; }
    setSavingId(a.asset_id);
    try {
      await axios.post(`${API}/assets/${a.asset_id}/price`, { price_usd: price }, { withCredentials: true });
      toast.success(price === 0 ? 'Set to free' : `Priced at $${price.toFixed(2)}`);
      setAssets(prev => prev.map(x => x.asset_id === a.asset_id ? { ...x, price_usd: price > 0 ? price : undefined } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingId(null); }
  };

  const toggleHidden = async (a) => {
    try {
      await axios.post(`${API}/marketplace/assets/${a.asset_id}/moderate`,
        { marketplace_hidden: !a.marketplace_hidden }, { withCredentials: true });
      setAssets(prev => prev.map(x => x.asset_id === a.asset_id ? { ...x, marketplace_hidden: !a.marketplace_hidden } : x));
      toast.success(a.marketplace_hidden ? 'Restored to marketplace' : 'Hidden from marketplace');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-marketplace-tab">
      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-amber-400" /> Marketplace Pricing
          </h3>
          <p className="text-xs text-zinc-500 max-w-xl">
            Override the USD price on any published marketplace asset. Set to 0 (empty) to make it free.
            Use <em>Hide</em> to keep an item off the public listing, or <em>Remove</em> to take it off the marketplace entirely.
          </p>
        </div>
        <Button
          onClick={() => { setShowBrowser(s => !s); if (!showBrowser) loadAllAssets(''); }}
          className="bg-emerald-500 text-black hover:bg-emerald-400 h-9 text-xs shrink-0"
          data-testid="admin-mkt-browse-all-btn"
        >
          {showBrowser ? 'Close browser' : '+ Add any user\u2019s asset'}
        </Button>
      </div>

      {showBrowser && (
        <div className="bg-[#0A0A0C] border border-emerald-500/30 rounded-xl p-4" data-testid="admin-all-assets-browser">
          <div className="flex items-center gap-2 mb-3">
            <Input
              value={browserQuery}
              onChange={(e) => setBrowserQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadAllAssets(browserQuery)}
              placeholder="Search by prompt / asset name / author email or name…"
              className="bg-[#1E1E24] border-[#2E2E34] text-white h-8 text-xs"
              data-testid="admin-all-assets-search"
            />
            <Button onClick={() => loadAllAssets(browserQuery)} className="h-8 text-xs bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 shrink-0">Search</Button>
          </div>
          {allLoading ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-400" /></div>
          ) : allAssets.length === 0 ? (
            <p className="text-xs text-zinc-500 text-center py-6">No assets match — try a different search or clear the query and press Enter.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[420px] overflow-y-auto">
              {allAssets.map(a => (
                <div key={a.asset_id} className="bg-[#121214] border border-[#1E1E24] rounded overflow-hidden group" data-testid={`admin-all-asset-${a.asset_id}`}>
                  <div className="aspect-square bg-[#0A0A0B] relative">
                    <img src={a.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {a.marketplace && (
                      <span className="absolute top-1 left-1 text-[9px] font-mono uppercase px-1 rounded bg-amber-500 text-black">On MKT</span>
                    )}
                  </div>
                  <div className="p-1.5">
                    <p className="text-[10px] text-white truncate" title={a.prompt}>{a.name || a.prompt || 'asset'}</p>
                    <p className="text-[9px] text-zinc-500 truncate" title={a.author_email}>{a.author_email || a.user_id || 'unknown'}</p>
                    <div className="flex gap-1 mt-1">
                      {a.marketplace ? (
                        <Button size="sm" onClick={() => unpublish(a)} className="flex-1 h-6 text-[9px] bg-zinc-700 text-white hover:bg-zinc-600" data-testid={`admin-all-unpublish-${a.asset_id}`}>Remove</Button>
                      ) : (
                        <Button size="sm" onClick={() => publish(a)} className="flex-1 h-6 text-[9px] bg-emerald-500 text-black hover:bg-emerald-400" data-testid={`admin-all-publish-${a.asset_id}`}>Add</Button>
                      )}
                      <Button size="sm" onClick={() => hardDelete(a)} className="h-6 w-6 p-0 bg-red-600/70 text-white hover:bg-red-600" data-testid={`admin-all-delete-${a.asset_id}`} title="Delete permanently">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#06B6D4]" /></div>
        ) : assets.length === 0 ? (
          <div className="py-16 text-center text-zinc-500 text-sm">No published marketplace assets yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1E1E24]">
                <tr>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-zinc-400 uppercase">Asset</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-zinc-400 uppercase">Type</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-zinc-400 uppercase">Current</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-zinc-400 uppercase">New Price (USD)</th>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-zinc-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E1E24]">
                {assets.map(a => (
                  <tr key={a.asset_id} className={`hover:bg-[#1E1E24]/50 ${a.marketplace_hidden ? 'opacity-50' : ''}`} data-testid={`admin-marketplace-row-${a.asset_id}`}>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <img src={a.url} alt="" className="w-10 h-10 rounded object-cover border border-[#1E1E24]" />
                        <div className="min-w-0">
                          <p className="text-xs text-white truncate max-w-[220px]" title={a.name || a.prompt}>{a.name || a.prompt || 'asset'}</p>
                          <p className="text-[10px] text-zinc-500 truncate max-w-[220px]" title={a.prompt}>{a.prompt}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3"><span className="text-xs font-mono text-zinc-400 uppercase">{a.asset_type}</span></td>
                    <td className="px-3 py-3">
                      <span className={`text-xs font-mono ${a.price_usd > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {a.price_usd > 0 ? `$${(a.price_usd || 0).toFixed(2)}` : 'FREE'}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <Input
                        type="number" min="0" max="999" step="0.5"
                        placeholder="0.00"
                        defaultValue={a.price_usd || ''}
                        onChange={(e) => setEditingPrice(prev => ({ ...prev, [a.asset_id]: e.target.value }))}
                        className="h-8 w-24 bg-[#1E1E24] border-[#2E2E34] text-white text-xs"
                        data-testid={`admin-price-input-${a.asset_id}`}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => save(a)}
                          disabled={savingId === a.asset_id}
                          className="h-7 text-xs bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90"
                          data-testid={`admin-price-save-${a.asset_id}`}
                        >
                          {savingId === a.asset_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => toggleHidden(a)}
                          className={`h-7 text-xs ${a.marketplace_hidden ? 'text-emerald-400' : 'text-red-400'} hover:text-white`}
                          data-testid={`admin-hide-${a.asset_id}`}
                        >
                          {a.marketplace_hidden ? 'Restore' : 'Hide'}
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => unpublish(a)}
                          className="h-7 text-xs text-zinc-300 hover:text-white"
                          data-testid={`admin-unpublish-${a.asset_id}`}
                          title="Take this asset off the marketplace (owner keeps a copy)"
                        >
                          Remove
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => hardDelete(a)}
                          className="h-7 text-xs text-red-400 hover:text-red-300"
                          data-testid={`admin-delete-${a.asset_id}`}
                          title="Permanently delete this asset for everyone"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                        <a href={a.url} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-md border border-[#1E1E24] flex items-center justify-center text-zinc-400 hover:text-[#06B6D4]" title="Open">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
